import { useDashboardSurveyList } from "./useDashboardSurveyList";
import { DASHBOARD_SURVEY_LIST_PAGE_SIZE } from "./dashboard-survey-list.constants";
import { Survey, SurveyStatus } from "../../../redux/survey/survey.types";
import { constant, times } from "lodash";
import DashboardSurveyFilter from "./dashboard-survey-filter.component";
import DashboardSurveyPagination from "./dashboard-survey-pagination.component";
import DashboardSurveyListEmpty from "./dashboard-survey-list-empty.component";
import SurveyCard from "./survey-card.component";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Button } from "src/components/ui/button";
import { Bot, Hammer, LayoutGrid, MessageCircleQuestion, Plus, Table as TableIcon } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { canEditSurvey } from "../../../redux/survey/survey.helpers";
import SurveyCardMenu from "./survey-card-menu.component";
import { Skeleton } from "src/components/ui/skeleton";
import useCreateNewSurvey from "src/hooks/useCreateNewSurvey";
import { hasSurveyCreateAccess } from "src/services/permission/permission.helpers";
import { useSelector } from "react-redux";
import { getUserStateActiveClient } from "src/redux/user/user.selectors";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import AISurveyCreationModal from "./ai-survey-creation-modal";

interface DashboardSurveyTableProps {
  onSurveyAction: (survey: Survey, action: 'assign-client') => void;
}

const DashboardSurveyTable = ({ onSurveyAction }: DashboardSurveyTableProps) => {
  const navigate = useNavigate();
  const { data, isFetching, refetch } = useDashboardSurveyList();
  const surveys = data?.surveys ?? [];
  const loadingArray = times(DASHBOARD_SURVEY_LIST_PAGE_SIZE, constant(undefined));
  const isEmpty = data?.totalObjects === 0;
  const [isTableView, setIsTableView] = useState(true);
  const { createNewSurvey } = useCreateNewSurvey();
  const { clientId } = useParams<{ clientId?: string }>();
  const client = useSelector(getUserStateActiveClient);
  const [isAISurveyCreationModalOpen, setIsAISurveyCreationModalOpen] = useState(false);

  const getStatusColor = (status: SurveyStatus, isDot: boolean = false) => {
    switch (status) {
      case SurveyStatus.Draft:
        return isDot ? 'bg-gray-600' : 'text-gray-500';
      case SurveyStatus.Live:
        return isDot ? 'bg-blue-500' : 'text-blue-500';
      case SurveyStatus.Complete:
        return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
      default:
        return isDot ? 'bg-gray-600' : 'text-gray-600';
    }
  };

  const canCreateSurvey = clientId || client ? hasSurveyCreateAccess({ permissions: client?.permissions }) : true;

  const handleRowClick = (survey: Survey | undefined) => {
    if (!survey) {
      return;
    }

    if (canEditSurvey(survey.surveyStatus)) {
      if (clientId) {
        navigate(`/client/${clientId}/surveys/create?ext-survey-id=${survey.extSurveyId}`);
      } else {
        navigate(`/surveys/create?ext-survey-id=${survey.extSurveyId}`);
      }
    } else {
      if (clientId) {
        navigate(`/client/${clientId}/results/overview/${survey.extSurveyId}`);
      } else {
        navigate(`/results/overview/${survey.extSurveyId}`);
      }
    }
  };

  const handleCreateNewSurvey = () => {
    void createNewSurvey();
    void refetch();
  };

  const renderTable = (surveys: (Survey | undefined)[]) => {
    return (
      <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex justify-between bg-gray-201">
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2 p-2 pl-4 items-center font-medium">
              <MessageCircleQuestion className="w-4 h-4" />
              Surveys
            </div>
          </div>
        </div>
        <Table>
          <TableHeader className="p-0 text-sm border-b border-gray-201">
            <TableRow className="border-none">
              <TableHead className="w-[78px] max-w-[78px] h-8" />
              <TableHead className="text-gray-600 h-8">
                Title
              </TableHead>
              <TableHead className="text-gray-600 h-8">
                Created
              </TableHead>
              <TableHead className="text-gray-600 text-center h-8">
                Questions
              </TableHead>
              <TableHead className="text-gray-600 text-center h-8">
                Starts
              </TableHead>
              <TableHead className="text-gray-600 text-center h-8">
                Completes
              </TableHead>
              <TableHead className="w-[30px] h-8" />
            </TableRow>
          </TableHeader>
          <TableBody className="rounded-none">
            {surveys.map((survey, index) => (
              <TableRow
                key={`survey-row-${index}`}
                onClick={() => handleRowClick(survey)}
                className={`hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                  index % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]'
                } cursor-pointer`}
              >
                <TableCell className={`text-center py-1.5 ${index === 0 ? 'rounded-none' : index === surveys.length - 1 ? 'rounded-none' : ''}`}>
                  {survey ? (
                    <span
                      className={`max-w-[78px] inline-flex items-center justify-center py-1 px-2 text-xs rounded-full font-bold bg-white shadow-sm ${getStatusColor(survey.surveyStatus)}`}
                    >
                      { survey.surveyStatus !== SurveyStatus.Draft && (<div className={`size-1 rounded-full mr-1 ${getStatusColor(survey.surveyStatus, true)}`} />)}
                      {survey.surveyStatus.charAt(0).toUpperCase() + survey.surveyStatus.slice(1)}
                    </span>
                  ) : (
                    <Skeleton className="w-16 h-[26px] mx-auto" />
                  )}
                </TableCell>
                <TableCell className="font-medium py-1.5">
                  {survey ? survey.title : <Skeleton className="w-48 h-4" />}
                </TableCell>
                <TableCell className="py-1.5">
                  {survey ? format(new Date(survey.createdAt), 'MMM d, yyyy') : <Skeleton className="w-24 h-4" />}
                </TableCell>
                <TableCell className="text-center py-1.5">
                  {survey ? survey.questionCount : <Skeleton className="w-8 h-4 mx-auto" />}
                </TableCell>
                <TableCell className="text-center py-1.5">
                  {survey ? (
                    (survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete) ? (
                      <span className="text-sm">
                        {survey.started.human + survey.started.ai}
                      </span>
                    ) : (
                      <span className="text-sm">
                        -
                      </span>
                    )
                  ) : <Skeleton className="w-8 h-4 mx-auto" />}
                </TableCell>
                <TableCell className="text-center py-1.5">
                  {survey ? (
                    (survey.surveyStatus === SurveyStatus.Live || survey.surveyStatus === SurveyStatus.Complete) ? (
                      <span className="text-sm">
                        {survey.completed.human + survey.completed.ai}
                      </span>
                    ) : (
                      <span className="text-sm">
                        -
                      </span>
                    )
                  ) : <Skeleton className="w-8 h-4 mx-auto" />}
                </TableCell>
                <TableCell
                  className={`${index === 0 ? '' : index === surveys.length - 1 ? '' : ''} py-1.5`}
                  onClick={e => e.stopPropagation()}
                >
                  {survey ? (
                    <SurveyCardMenu
                      survey={survey}
                      onSurveyAction={onSurveyAction}
                    />
                  ) : <Skeleton className="w-6 h-6 mx-auto" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderCards = (surveys: (Survey | undefined)[]) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
        {surveys.map((survey, index) => (
          <SurveyCard
            survey={survey}
            key={`survey-card-${index}`}
            index={index}
            onSurveyAction={onSurveyAction}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className="flex flex-col gap-4 grow"
      id="survey-list"
    >
      <div className="flex items-center justify-end gap-4">
        <div className="flex-1">
          <DashboardSurveyFilter />
        </div>
        <div className="flex items-center gap-1 p-1">
          <Button
            variant={isTableView ? "primary" : "ghost"}
            size="med"
            className={`${!isTableView ? "text-black/60" : ""}`}
            onClick={() => setIsTableView(true)}
          >
            <TableIcon className="h-4 w-4" />
          </Button>
          <Button
            variant={!isTableView ? "primary" : "ghost"}
            size="med"
            className={`${isTableView ? "text-black/60" : ""} hover:bg-blue-500 hover:text-white hover:opacity-75`}
            onClick={() => setIsTableView(false)}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
        {canCreateSurvey && (
          <>
            <div className="h-8 border-l-1 border-black/10 -mx-1 hidden lg:block" />
            <div className="flex items-center gap-1 p-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="bg-gray-201 text-black hover:bg-blue-500 hover:text-white"
                    variant="gray"
                    size="med"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onClick={handleCreateNewSurvey}
                  >
                    <div className="flex items-center gap-2">
                      <Hammer className="w-4 h-4" />
                      Build from Scratch
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setIsAISurveyCreationModalOpen(true)}
                  >
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4" />
                      Generate with AI
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </div>
      {isEmpty ? <DashboardSurveyListEmpty /> : isTableView ? renderTable(isFetching ? loadingArray : surveys) : renderCards(isFetching ? loadingArray : surveys)}
      <DashboardSurveyPagination />

      <AISurveyCreationModal
        isOpen={isAISurveyCreationModalOpen}
        onClose={() => setIsAISurveyCreationModalOpen(false)}
      />
    </div>
  );
};

export default DashboardSurveyTable;